import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCalendarAlt, FaClock, FaTicketAlt, FaArrowLeft,
  FaTrophy, FaCoins, FaCheckCircle, FaMedal, FaStar,
  FaLock, FaHourglassHalf, FaTag, FaUsers, FaGift,
} from "react-icons/fa";
import { MdCampaign } from "react-icons/md";
import Navbar from "../components/Navbar";

const BASE_URL = "https://api.dreamtoday.in";

/* ═══════════════════════════════════════════════════
   PALETTE
═══════════════════════════════════════════════════ */
const G = {
  gold: "#C9A84C",
  goldDeep: "#A8863A",
  goldLight: "#E8C96A",
  goldGlow: "rgba(201,168,76,0.22)",
  cream: "#FFFDF5",
  creamDeep: "#FFF8E7",
  creamMid: "#FFF1CC",
  creamBorder: "#F0D98C",
  text: "#5C4A1E",
  textMid: "#8A6E34",
  textLight: "#B89A5A",
};

/* ═══════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════ */
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

/* ═══════════════════════════════════════════════════
   COUNTDOWN ENGINE
═══════════════════════════════════════════════════ */
const calcTimeLeft = (targetDate) => {
  const diff = new Date(targetDate) - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
    expired: false,
  };
};

const useCountdown = (targetDate) => {
  const [time, setTime] = useState(() => calcTimeLeft(targetDate));
  useEffect(() => {
    if (!targetDate) return;
    setTime(calcTimeLeft(targetDate));
    const t = setInterval(() => setTime(calcTimeLeft(targetDate)), 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  return time;
};

/* ═══════════════════════════════════════════════════
   SMART TIMER
═══════════════════════════════════════════════════ */
const SmartTimer = ({ registrationEndDate, endDate }) => {
  const regExpired = !registrationEndDate || new Date(registrationEndDate) <= new Date();
  const activeDate = regExpired ? endDate : registrationEndDate;
  const { days, hours, minutes, seconds, expired } = useCountdown(activeDate);

  const isRegPhase = !regExpired;
  const phaseLabel = isRegPhase ? "Registration closes in" : "Campaign ends in";
  const PhaseIcon = isRegPhase ? FaClock : FaHourglassHalf;
  const digitColor = isRegPhase ? G.goldDeep : "#7C5C20";
  const blockBg = isRegPhase
    ? `linear-gradient(135deg,${G.goldLight}40,${G.gold}22)`
    : "rgba(200,155,30,0.14)";
  const containerBg = isRegPhase ? G.creamMid : "#FFF4D6";
  const containerBorder = isRegPhase ? G.creamBorder : "#DDB840";

  const units = days > 0
    ? [{ v: days, l: "Days" }, { v: hours, l: "Hrs" }, { v: minutes, l: "Min" }]
    : [{ v: hours, l: "Hrs" }, { v: minutes, l: "Min" }, { v: seconds, l: "Sec" }];

  return (
    <AnimatePresence mode="wait">
      {expired ? (
        <motion.div
          key="ended"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="flex items-center justify-center gap-2 rounded-2xl py-4"
          style={{ background: "#FFF0F0", border: "1px solid #FFCDD2" }}
        >
          <FaLock style={{ color: "#C0392B", fontSize: 13 }} />
          <span className="text-sm font-bold tracking-wide" style={{ color: "#C0392B" }}>
            Campaign Ended
          </span>
        </motion.div>
      ) : (
        <motion.div
          key={isRegPhase ? "reg" : "end"}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
          className="rounded-2xl overflow-hidden"
          style={{ background: containerBg, border: `1px solid ${containerBorder}` }}
        >
          <div className="flex items-center gap-1.5 px-4 pt-3 pb-1.5">
            <PhaseIcon style={{ color: digitColor, fontSize: 10 }} />
            <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: digitColor }}>
              {phaseLabel}
            </span>
          </div>
          <div className="flex items-center justify-center gap-2 px-4 pb-4">
            {units.map(({ v, l }, i) => (
              <React.Fragment key={l}>
                <div className="flex flex-col items-center" style={{ minWidth: 52 }}>
                  <div
                    className="w-full flex items-center justify-center rounded-xl"
                    style={{ background: blockBg, border: `1px solid ${containerBorder}`, padding: "10px 0" }}
                  >
                    <span className="text-2xl font-black tabular-nums" style={{ color: digitColor }}>
                      {String(v).padStart(2, "0")}
                    </span>
                  </div>
                  <span className="text-[9px] font-bold mt-1.5 uppercase tracking-wider" style={{ color: G.textLight }}>
                    {l}
                  </span>
                </div>
                {i < units.length - 1 && (
                  <span className="text-xl font-black mb-5 select-none" style={{ color: G.goldLight }}>:</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ═══════════════════════════════════════════════════
   PRIZE POSITION CONFIG
═══════════════════════════════════════════════════ */
const PRIZE_CONFIG = {
  1: {
    label: "1st Prize",
    medalBg: "linear-gradient(135deg,#FFD700,#F59E0B)",
    medalColor: "#7A4F00",
    cardBorder: "#F0D98C",
    cardBg: `linear-gradient(160deg,#FFFDF5,#FFF8E7,#FFF3CC)`,
    glow: "0 8px 32px rgba(201,168,76,0.28)",
    accentBg: "#FFF3CC",
    accentText: "#A8863A",
    Icon: FaTrophy,
  },
  2: {
    label: "2nd Prize",
    medalBg: "linear-gradient(135deg,#D1D5DB,#9CA3AF)",
    medalColor: "#374151",
    cardBorder: "#D1D5DB",
    cardBg: "linear-gradient(160deg,#F9FAFB,#F3F4F6)",
    glow: "0 6px 24px rgba(156,163,175,0.22)",
    accentBg: "#F3F4F6",
    accentText: "#6B7280",
    Icon: FaMedal,
  },
  3: {
    label: "3rd Prize",
    medalBg: "linear-gradient(135deg,#D97706,#92400E)",
    medalColor: "#FFF8E7",
    cardBorder: "#D97706",
    cardBg: "linear-gradient(160deg,#FFFBEB,#FEF3C7)",
    glow: "0 6px 24px rgba(217,119,6,0.2)",
    accentBg: "#FEF3C7",
    accentText: "#92400E",
    Icon: FaStar,
  },
};

const getPrizeConfig = (pos) =>
  PRIZE_CONFIG[pos] || {
    label: `${pos}th Prize`,
    medalBg: `linear-gradient(135deg,${G.goldLight},${G.gold})`,
    medalColor: "#3B2A0A",
    cardBorder: G.creamBorder,
    cardBg: `linear-gradient(160deg,${G.cream},${G.creamDeep})`,
    glow: `0 4px 16px ${G.goldGlow}`,
    accentBg: G.creamMid,
    accentText: G.goldDeep,
    Icon: FaStar,
  };

/* ═══════════════════════════════════════════════════
   INFO ROW
═══════════════════════════════════════════════════ */
const InfoRow = ({ icon: Icon, label, value }) => (
  <div
    className="flex items-center gap-3 py-3 px-4 rounded-2xl"
    style={{ background: G.creamMid, border: `1px solid ${G.creamBorder}` }}
  >
    <div
      className="flex-shrink-0 flex items-center justify-center rounded-xl"
      style={{ width: 32, height: 32, background: `linear-gradient(135deg,${G.goldLight},${G.gold})` }}
    >
      <Icon style={{ color: "#3B2A0A", fontSize: 12 }} />
    </div>
    <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
      <span className="text-xs font-semibold" style={{ color: G.textLight }}>{label}</span>
      <span className="text-sm font-bold text-right" style={{ color: G.text }}>{value}</span>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════
   LOADING SCREEN
═══════════════════════════════════════════════════ */
const LoadingScreen = () => (
  <>
    <Navbar />
    <div className="min-h-screen flex flex-col items-center justify-center gap-4"
      style={{ background: `linear-gradient(180deg,${G.cream},${G.creamDeep},${G.cream})` }}>
      <div
        className="rounded-full border-4 border-t-transparent animate-spin"
        style={{ width: 48, height: 48, borderColor: `${G.gold} transparent ${G.gold} ${G.gold}` }}
      />
      <p className="text-sm font-semibold" style={{ color: G.textMid }}>Loading campaign…</p>
    </div>
  </>
);

/* ═══════════════════════════════════════════════════
   ERROR SCREEN
═══════════════════════════════════════════════════ */
const ErrorScreen = ({ message, onBack }) => (
  <>
    <Navbar />
    <div className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ background: `linear-gradient(180deg,${G.cream},${G.creamDeep},${G.cream})` }}>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center">
        <div
          className="rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          style={{ width: 96, height: 96, background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})`, border: `2px solid ${G.creamBorder}` }}
        >
          <MdCampaign style={{ color: G.gold, fontSize: 40 }} />
        </div>
        <h2 className="text-2xl font-black mb-2" style={{ color: G.text }}>Campaign Not Found</h2>
        <p className="text-sm mb-6 max-w-xs mx-auto" style={{ color: G.textMid }}>
          {message || "This campaign doesn't exist or may have been removed."}
        </p>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition hover:scale-105"
          style={{ background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})`, color: "#3B2A0A", boxShadow: `0 4px 16px ${G.goldGlow}` }}
        >
          <FaArrowLeft /> Go Back
        </button>
      </motion.div>
    </div>
  </>
);

/* ═══════════════════════════════════════════════════
   TICKET BUYER — registration-gated action zone
═══════════════════════════════════════════════════ */
const TicketBuyZone = ({ campaign, id, onSuccess }) => {
  const [ticketCount, setTicketCount] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [buyError, setBuyError] = useState("");

  /* ── derive registration state ── */
  const now = Date.now();

  // Registration is closed if the flag is set OR the registrationEndDate has passed
  const regClosedByFlag = campaign.isRegistrationClosed === true;
  const regClosedByDate =
    campaign.registrationEndDate && now > new Date(campaign.registrationEndDate).getTime();
  const registrationOpen = !regClosedByFlag && !regClosedByDate;

  // Campaign hasn't started yet
  const campaignNotStarted = now < new Date(campaign.startDate).getTime();

  // Campaign has fully ended
  const campaignEnded = now > new Date(campaign.endDate).getTime();

  // No tickets left
  const soldOut = (campaign.remainingTickets ?? campaign.noOfTickets) <= 0;

  const remaining = campaign.remainingTickets ?? campaign.noOfTickets;

  const handleBuy = async () => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) { setBuyError("Please login to buy tickets."); return; }
    setProcessing(true); setBuyError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/bookcamp/${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaignId: id, numberOfTickets: ticketCount }),
      });
      const data = await res.json();
      if (data.success) onSuccess(ticketCount);
      else setBuyError(data.message || "Booking failed. Please try again.");
    } catch { setBuyError("Network error. Please try again."); }
    finally { setProcessing(false); }
  };

  /* ── CASE 1: Campaign fully ended ── */
  if (campaignEnded) {
    return (
      <div
        className="rounded-2xl p-5 text-center"
        style={{ background: G.creamMid, border: `1.5px solid ${G.creamBorder}` }}
      >
        <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ background: `linear-gradient(135deg,${G.creamBorder},${G.gold})` }}>
          <FaTrophy style={{ color: "#3B2A0A", fontSize: 18 }} />
        </div>
        <h3 className="font-extrabold text-base mb-1" style={{ color: G.text }}>Campaign Completed</h3>
        <p className="text-sm mb-4" style={{ color: G.textMid }}>
          This campaign ended on <strong>{formatDate(campaign.endDate)}</strong>. Check out other active campaigns!
        </p>
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition hover:scale-105"
          style={{
            background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})`,
            color: "#3B2A0A",
            boxShadow: `0 4px 14px ${G.goldGlow}`,
          }}
        >
          <FaTicketAlt /> Browse More Campaigns
        </Link>
      </div>
    );
  }

  /* ── CASE 2: Sold out ── */
  if (soldOut) {
    return (
      <div
        className="flex items-center justify-center gap-2 px-4 py-4 rounded-2xl text-sm font-semibold"
        style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#B91C1C" }}
      >
        🚫 Tickets Sold Out — No remaining tickets for this campaign.
      </div>
    );
  }

  /* ── CASE 3: Registration closed (flag or date), but campaign still ongoing ── */
  if (!registrationOpen) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-5"
        style={{ background: "#FFFBEB", border: "1.5px solid #FDE68A" }}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "#FEF3C7" }}>
            <FaLock style={{ color: "#D97706", fontSize: 16 }} />
          </div>
          <div>
            <h3 className="font-extrabold text-sm mb-1" style={{ color: "#92400E" }}>
              Registration Closed
            </h3>
            <p className="text-xs leading-relaxed" style={{ color: "#B45309" }}>
              {regClosedByFlag
                ? "The organiser has manually closed registrations for this campaign."
                : `Registration closed on ${formatDate(campaign.registrationEndDate)}. You can no longer purchase tickets.`}
            </p>
            {!campaignEnded && (
              <div
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-xl text-[11px] font-bold"
                style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}
              >
                <FaClock style={{ fontSize: 9 }} />
                Campaign runs until {formatDate(campaign.endDate)}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  /* ── CASE 4: Campaign not started yet — pre-sale / upcoming ── */
  if (campaignNotStarted) {
    return (
      <div className="flex flex-col gap-3">
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm"
          style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1D4ED8" }}
        >
          <FaTicketAlt style={{ flexShrink: 0, fontSize: 12 }} />
          <span className="font-medium">
            Pre-sale open! Campaign starts <strong>{formatDate(campaign.startDate)}</strong>
          </span>
        </div>

        <TicketSelector
          ticketCount={ticketCount}
          setTicketCount={setTicketCount}
          max={remaining}
          price={campaign.price}
        />

        {buyError && <BuyError message={buyError} />}

        <BuyButton
          processing={processing}
          ticketCount={ticketCount}
          price={campaign.price}
          onClick={handleBuy}
        />

        <p className="text-center text-[11px]" style={{ color: G.textLight }}>
          You are booking ahead — tickets are locked in at today's price.
        </p>
      </div>
    );
  }

  /* ── CASE 5: Registration OPEN + Campaign ACTIVE — normal buy flow ── */
  return (
    <div className="flex flex-col gap-3">
      {/* Registration open banner */}
      <div
        className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm"
        style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534" }}
      >
        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
        <span className="font-semibold">
          Registration open — <strong>{remaining.toLocaleString()}</strong> ticket{remaining !== 1 ? "s" : ""} remaining
        </span>
      </div>

      <TicketSelector
        ticketCount={ticketCount}
        setTicketCount={setTicketCount}
        max={remaining}
        price={campaign.price}
      />

      {buyError && <BuyError message={buyError} />}

      <BuyButton
        processing={processing}
        ticketCount={ticketCount}
        price={campaign.price}
        onClick={handleBuy}
      />

      {campaign.registrationEndDate && (
        <p className="text-center text-[11px]" style={{ color: G.textLight }}>
          Registration closes on <strong>{formatDate(campaign.registrationEndDate)}</strong>
        </p>
      )}
    </div>
  );
};

/* ── Shared sub-components ── */
const TicketSelector = ({ ticketCount, setTicketCount, max, price }) => (
  <div className="flex items-center gap-3">
    <span className="text-sm font-semibold flex-shrink-0" style={{ color: G.textMid }}>Qty:</span>
    <div
      className="flex items-center rounded-2xl overflow-hidden"
      style={{ border: `1.5px solid ${G.creamBorder}` }}
    >
      <button
        onClick={() => setTicketCount((p) => Math.max(1, p - 1))}
        className="px-4 py-2 font-bold text-lg transition hover:scale-110"
        style={{ background: G.creamMid, color: G.goldDeep }}
      >−</button>
      <span className="px-5 py-2 font-black text-base" style={{ color: G.text }}>
        {ticketCount}
      </span>
      <button
        onClick={() => setTicketCount((p) => Math.min(max, p + 1))}
        className="px-4 py-2 font-bold text-lg transition hover:scale-110"
        style={{ background: G.creamMid, color: G.goldDeep }}
      >+</button>
    </div>
    <span className="font-black text-base" style={{ color: G.goldDeep }}>
      = ₹{(price * ticketCount).toLocaleString()}
    </span>
  </div>
);

const BuyError = ({ message }) => (
  <div
    className="px-4 py-3 rounded-2xl text-sm"
    style={{ background: "#FFF5F5", border: "1px solid #FFCDD2", color: "#C62828" }}
  >
    ⚠️ {message}
  </div>
);

const BuyButton = ({ processing, ticketCount, price, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    disabled={processing}
    className="w-full py-4 rounded-2xl font-extrabold text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
    style={{
      background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})`,
      color: "#3B2A0A",
      boxShadow: `0 6px 20px ${G.goldGlow}`,
    }}
  >
    {processing ? (
      <>
        <div
          className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: "#3B2A0A transparent #3B2A0A #3B2A0A" }}
        />
        Processing…
      </>
    ) : (
      <>
        <FaTicketAlt />
        Buy {ticketCount} Ticket{ticketCount > 1 ? "s" : ""} — ₹{(price * ticketCount).toLocaleString()}
      </>
    )}
  </motion.button>
);

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccess] = useState(false);
  const [bookedCount, setBookedCount] = useState(1);

  useEffect(() => {
    if (!id) { setError("Invalid campaign ID."); setLoading(false); return; }
    fetch(`${BASE_URL}/api/admin/getsinglecampaign/${id}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.campaign) setCampaign(d.campaign);
        else setError(d.message || "Campaign not found.");
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingScreen />;
  if (error || !campaign) return <ErrorScreen message={error} onBack={() => navigate(-1)} />;

  /* ── derive display phase (for status badge + progress only) ── */
  const now = Date.now();
  const isUpcoming = now < new Date(campaign.startDate).getTime();
  const isCompleted =
    now > new Date(campaign.endDate).getTime() || campaign.isRegistrationClosed;
  const isCurrent = !isUpcoming && !isCompleted;

  const ticketsSold = campaign.noOfTickets - (campaign.remainingTickets ?? 0);
  const progress = campaign.noOfTickets > 0
    ? Math.min((ticketsSold / campaign.noOfTickets) * 100, 100) : 0;

  const sortedPrizes = [...(campaign.prizes || [])].sort(
    (a, b) => (a.position ?? 99) - (b.position ?? 99)
  );

  return (
    <>
      <Navbar />

      <div
        className="min-h-screen"
        style={{
          background: `linear-gradient(180deg,${G.cream} 0%,${G.creamDeep} 40%,${G.cream} 100%)`,
          paddingTop: "clamp(80px,10vw,100px)",
          paddingBottom: "clamp(48px,8vw,80px)",
        }}
      >
        {/* subtle bg orbs */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="absolute rounded-full" style={{ top: -200, right: -200, width: 500, height: 500, background: "radial-gradient(circle,rgba(240,217,140,0.22),transparent 70%)" }} />
          <div className="absolute rounded-full" style={{ bottom: -200, left: -200, width: 500, height: 500, background: "radial-gradient(circle,rgba(240,217,140,0.22),transparent 70%)" }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* BACK BUTTON */}
          <motion.button
            initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm mb-8 transition hover:scale-105"
            style={{
              background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})`,
              color: "#3B2A0A",
              boxShadow: `0 4px 14px ${G.goldGlow}`,
            }}
          >
            <FaArrowLeft style={{ fontSize: 11 }} /> Back
          </motion.button>

          {/* ═══ HERO SECTION ═══ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 mb-12 lg:mb-20">

            {/* LEFT — IMAGE */}
            <motion.div
              initial={{ opacity: 0, x: -32 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div
                className="relative overflow-hidden rounded-3xl shadow-xl"
                style={{
                  aspectRatio: "4/3",
                  border: `1.5px solid ${G.creamBorder}`,
                  boxShadow: `0 16px 48px rgba(201,168,76,0.18), 0 4px 16px rgba(0,0,0,0.06)`,
                }}
              >
                {campaign.images?.[0] ? (
                  <img
                    src={`${BASE_URL}${campaign.images[0]}`}
                    alt={campaign.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-full flex flex-col items-center justify-center"
                    style={{ background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})` }}
                  >
                    <FaTrophy style={{ color: G.goldLight, fontSize: 60, opacity: 0.4, marginBottom: 12 }} />
                    <p className="text-sm font-medium" style={{ color: G.textLight }}>No Image Available</p>
                  </div>
                )}

                {/* dark bottom fade */}
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top,rgba(60,38,4,0.55) 0%,transparent 45%)" }} />

                {/* status badge */}
                <div className="absolute top-4 right-4">
                  {isUpcoming && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black tracking-wider"
                      style={{ background: "rgba(219,234,254,0.95)", color: "#1D4ED8", backdropFilter: "blur(8px)", border: "1px solid rgba(147,197,253,0.6)" }}>
                      🔜 UPCOMING
                    </span>
                  )}
                  {isCurrent && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black tracking-wider"
                      style={{ background: "rgba(220,252,231,0.95)", color: "#166534", backdropFilter: "blur(8px)", border: "1px solid rgba(134,239,172,0.6)" }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> LIVE
                    </span>
                  )}
                  {isCompleted && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black tracking-wider"
                      style={{ background: "rgba(255,252,235,0.95)", color: G.goldDeep, backdropFilter: "blur(8px)", border: `1px solid ${G.creamBorder}` }}>
                      <FaLock style={{ fontSize: 8 }} /> ENDED
                    </span>
                  )}
                </div>

                {/* category badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 rounded-full text-[10px] font-black tracking-widest"
                    style={{ background: "rgba(255,252,235,0.93)", color: G.goldDeep, backdropFilter: "blur(8px)", border: `1px solid ${G.creamBorder}` }}>
                    {campaign.categoryName}
                  </span>
                </div>

                {/* title overlay */}
                <div className="absolute bottom-0 left-0 right-0 px-5 pb-5">
                  <h1
                    className="font-black leading-tight"
                    style={{ color: "#FFF8E7", textShadow: "0 2px 12px rgba(0,0,0,0.5)", fontSize: "clamp(18px,3vw,28px)" }}
                  >
                    {campaign.title}
                  </h1>
                </div>
              </div>

              {/* stats strip below image */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {[
                  { label: "Ticket Price", value: `₹${campaign.price}` },
                  { label: "Total Tickets", value: campaign.noOfTickets.toLocaleString() },
                  { label: "Winners", value: campaign.noOfWinners },
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex flex-col items-center justify-center py-3 rounded-2xl"
                    style={{ background: G.creamMid, border: `1px solid ${G.creamBorder}` }}
                  >
                    <span className="text-lg font-black leading-none" style={{ color: G.goldDeep }}>{value}</span>
                    <span className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: G.textLight }}>{label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* RIGHT — DETAILS */}
            <motion.div
              initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="flex flex-col gap-5"
            >
              {/* title (mobile only) */}
              <div className="lg:hidden">
                <h1 className="font-black leading-tight mb-1" style={{ color: G.text, fontSize: "clamp(22px,5vw,32px)" }}>
                  {campaign.title}
                </h1>
              </div>

              {campaign.description && (
                <p className="text-sm leading-relaxed" style={{ color: G.textMid }}>{campaign.description}</p>
              )}

              {/* SMART TIMER */}
              <SmartTimer
                registrationEndDate={campaign.registrationEndDate}
                endDate={campaign.endDate}
              />

              {/* INFO GRID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <InfoRow icon={MdCampaign} label="Category" value={<span className="capitalize">{campaign.categoryName}</span>} />
                <InfoRow icon={FaCheckCircle} label="Status" value={<span className="capitalize">{campaign.status}</span>} />
                <InfoRow icon={FaCalendarAlt} label="Start Date" value={formatDate(campaign.startDate)} />
                <InfoRow icon={FaClock} label="End Date" value={formatDate(campaign.endDate)} />
                {campaign.registrationEndDate && (
                  <InfoRow icon={FaCalendarAlt} label="Registration Ends" value={formatDate(campaign.registrationEndDate)} />
                )}
                <InfoRow icon={FaCoins} label="Ticket Price" value={`₹${campaign.price}`} />
                <InfoRow icon={FaTicketAlt} label="Total Tickets" value={campaign.noOfTickets.toLocaleString()} />
                <InfoRow icon={FaUsers} label="No. of Winners" value={campaign.noOfWinners} />
              </div>

              {/* PROGRESS BAR */}
              <div
                className="rounded-2xl p-4"
                style={{ background: G.creamMid, border: `1px solid ${G.creamBorder}` }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold" style={{ color: G.textMid }}>Tickets Sold</span>
                  <span className="text-xs font-black" style={{ color: G.goldDeep }}>
                    {ticketsSold} / {campaign.noOfTickets}
                  </span>
                </div>
                <div className="w-full rounded-full overflow-hidden" style={{ height: 8, background: G.creamBorder }}>
                  <motion.div
                    initial={{ width: 0 }} animate={{ width: `${progress}%` }}
                    transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
                    className="h-full rounded-full"
                    style={{ background: `linear-gradient(90deg,${G.goldLight},${G.goldDeep})` }}
                  />
                </div>
              </div>

              {/* ── REGISTRATION-GATED ACTION ZONE ── */}
              <TicketBuyZone
                campaign={campaign}
                id={id}
                onSuccess={(count) => {
                  setBookedCount(count);
                  setShowSuccess(true);
                }}
              />
            </motion.div>
          </div>

          {/* ═══ PRIZES SECTION ═══ */}
          {sortedPrizes.length > 0 && (
            <div>
              {/* section header */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                  className="inline-flex items-center gap-2 rounded-full mb-4"
                  style={{
                    background: G.creamMid, color: G.goldDeep,
                    border: `1.5px solid ${G.creamBorder}`,
                    boxShadow: `0 2px 12px ${G.goldGlow}`,
                    padding: "6px 16px", fontSize: 10, fontWeight: 900,
                    letterSpacing: "0.12em", textTransform: "uppercase",
                  }}
                >
                  <FaGift style={{ color: G.gold, fontSize: 9 }} />
                  Prize Pool
                  <FaGift style={{ color: G.gold, fontSize: 9 }} />
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: 0.07 }}
                  className="font-black leading-none tracking-tight"
                  style={{ color: G.text, fontSize: "clamp(24px,4vw,44px)" }}
                >
                  Winning
                  <span
                    className="block mt-1"
                    style={{
                      background: `linear-gradient(90deg,${G.goldDeep},${G.gold},${G.goldLight},${G.gold})`,
                      WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                    }}
                  >
                    Prizes
                  </span>
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.13 }}
                  className="mt-2 text-sm" style={{ color: G.textMid }}
                >
                  {campaign.noOfWinners} winner{campaign.noOfWinners !== 1 ? "s" : ""} will walk away with these prizes
                </motion.p>

                {/* divider */}
                <div className="flex items-center justify-center gap-2.5 mt-6">
                  <div style={{ height: 1, width: "clamp(24px,5vw,80px)", background: `linear-gradient(to right,transparent,${G.creamBorder})` }} />
                  <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
                  <div style={{ height: 1, width: 16, background: G.creamBorder }} />
                  <FaTrophy style={{ color: G.gold, fontSize: 11 }} />
                  <div style={{ height: 1, width: 16, background: G.creamBorder }} />
                  <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
                  <div style={{ height: 1, width: "clamp(24px,5vw,80px)", background: `linear-gradient(to left,transparent,${G.creamBorder})` }} />
                </div>
              </div>

              {/* PRIZE CARDS GRID */}
              <div
                className="grid gap-5 sm:gap-6"
                style={{
                  gridTemplateColumns: sortedPrizes.length === 1
                    ? "1fr"
                    : sortedPrizes.length === 2
                      ? "repeat(2,1fr)"
                      : "repeat(auto-fit,minmax(min(100%,280px),1fr))",
                }}
              >
                {sortedPrizes.map((prize, idx) => {
                  const pos = prize.position ?? idx + 1;
                  const cfg = getPrizeConfig(pos);
                  const Icon = cfg.Icon;
                  const isFirst = pos === 1;

                  return (
                    <motion.div
                      key={prize._id}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      whileHover={{ y: -6, transition: { duration: 0.22 } }}
                      className="relative rounded-3xl overflow-hidden flex flex-col"
                      style={{
                        background: cfg.cardBg,
                        border: `1.5px solid ${cfg.cardBorder}`,
                        boxShadow: cfg.glow,
                      }}
                    >
                      {/* PRIZE IMAGE */}
                      <div
                        className="relative overflow-hidden flex-shrink-0"
                        style={{ height: isFirst ? 240 : 200, background: G.creamMid }}
                      >
                        {prize.prizeImage ? (
                          <img
                            src={`${BASE_URL}${prize.prizeImage}`}
                            alt={prize.prizeName}
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                          />
                        ) : (
                          <div
                            className="w-full h-full flex flex-col items-center justify-center"
                            style={{ background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})` }}
                          >
                            <FaTrophy style={{ color: G.goldLight, fontSize: 40, opacity: 0.4, marginBottom: 8 }} />
                            <span className="text-xs font-medium" style={{ color: G.textLight }}>{prize.prizeName}</span>
                          </div>
                        )}

                        <div className="absolute inset-0" style={{ background: "linear-gradient(to top,rgba(60,38,4,0.55) 0%,transparent 50%)" }} />

                        <div
                          className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black shadow-md"
                          style={{ background: cfg.medalBg, color: cfg.medalColor }}
                        >
                          <Icon style={{ fontSize: 10 }} />
                          {cfg.label}
                        </div>

                        <div
                          className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider"
                          style={{ background: "rgba(255,252,235,0.92)", color: G.goldDeep, border: `1px solid ${G.creamBorder}`, backdropFilter: "blur(8px)" }}
                        >
                          {prize.prizeType === "cash" ? "💰 Cash" : "🎁 Product"}
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
                          <h3
                            className="font-extrabold leading-snug"
                            style={{ color: "#FFF8E7", textShadow: "0 1px 8px rgba(0,0,0,0.5)", fontSize: "clamp(14px,2vw,17px)" }}
                          >
                            {prize.prizeName}
                          </h3>
                        </div>
                      </div>

                      {/* PRIZE BODY */}
                      <div className="flex flex-col flex-1 p-4 gap-3">
                        {prize.prizeType === "cash" && prize.prizeAmount ? (
                          <div
                            className="flex items-center gap-2 px-3 py-2.5 rounded-2xl"
                            style={{ background: cfg.accentBg, border: `1px solid ${cfg.cardBorder}` }}
                          >
                            <FaCoins style={{ color: cfg.accentText, fontSize: 12, flexShrink: 0 }} />
                            <span className="font-black text-lg" style={{ color: cfg.accentText }}>
                              ₹{prize.prizeAmount.toLocaleString()}
                            </span>
                          </div>
                        ) : prize.description ? (
                          <div
                            className="flex items-start gap-2 px-3 py-2.5 rounded-2xl"
                            style={{ background: cfg.accentBg, border: `1px solid ${cfg.cardBorder}` }}
                          >
                            <FaTag style={{ color: cfg.accentText, fontSize: 10, flexShrink: 0, marginTop: 2 }} />
                            <span className="text-sm font-semibold" style={{ color: cfg.accentText }}>
                              {prize.description}
                            </span>
                          </div>
                        ) : null}

                        {prize.prizeCategory && (
                          <div className="flex items-center gap-1.5">
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold capitalize"
                              style={{ background: G.creamMid, color: G.textMid, border: `1px solid ${G.creamBorder}` }}
                            >
                              <FaGift style={{ fontSize: 8 }} />
                              {prize.prizeCategory}
                            </span>
                          </div>
                        )}

                        {isFirst && (
                          <div
                            className="flex items-center gap-2 px-3 py-2 rounded-2xl mt-auto"
                            style={{ background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})`, border: `1px solid ${G.creamBorder}` }}
                          >
                            <FaTrophy style={{ color: G.gold, fontSize: 11, flexShrink: 0 }} />
                            <span className="text-[11px] font-black" style={{ color: G.goldDeep }}>
                              Grand Prize — Top Winner Takes This!
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ WINNERS SECTION ═══ */}
          {campaign.winners?.length > 0 && (
            <div className="mt-16">
              <div className="text-center mb-8">
                <h2
                  className="font-black leading-none mb-2"
                  style={{ color: G.text, fontSize: "clamp(22px,4vw,40px)" }}
                >
                  🏆 <span style={{
                    background: `linear-gradient(90deg,${G.goldDeep},${G.gold},${G.goldLight})`,
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                  }}>Campaign Winners</span>
                </h2>
                <p className="text-sm" style={{ color: G.textMid }}>Congratulations to all the winners!</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {campaign.winners.map((winner, idx) => {
                  const pos = winner.prizePosition || idx + 1;
                  const medal = {
                    1: { bg: "linear-gradient(135deg,#FFD700,#F59E0B)", color: "#7A4F00" },
                    2: { bg: "linear-gradient(135deg,#D1D5DB,#9CA3AF)", color: "#374151" },
                    3: { bg: "linear-gradient(135deg,#D97706,#92400E)", color: "#FFF8E7" },
                  }[pos] || { bg: `linear-gradient(135deg,${G.goldLight},${G.gold})`, color: "#3B2A0A" };

                  return (
                    <motion.div
                      key={winner._id}
                      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      transition={{ delay: idx * 0.08 }}
                      className="flex items-center gap-3 p-4 rounded-2xl"
                      style={{
                        background: pos === 1 ? `linear-gradient(135deg,${G.creamMid},${G.creamBorder})` : G.creamDeep,
                        border: `1.5px solid ${pos === 1 ? G.creamBorder : G.creamBorder + "88"}`,
                        boxShadow: pos === 1 ? `0 4px 16px ${G.goldGlow}` : "none",
                      }}
                    >
                      <div className="flex-shrink-0 flex items-center justify-center rounded-xl font-black"
                        style={{ width: 40, height: 40, background: medal.bg, color: medal.color, boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
                        <FaMedal style={{ fontSize: 16 }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-extrabold text-sm truncate" style={{ color: G.text }}>{winner.userName}</p>
                        <p className="text-[11px] truncate" style={{ color: G.textMid }}>{winner.winningGift}</p>
                        {winner.ticketIds?.length > 0 && (
                          <p className="text-[10px] mt-0.5" style={{ color: G.textLight }}>
                            Ticket: {winner.ticketIds[0]}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {winner.status === "claimed" ? (
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "#DCFCE7", color: "#166534" }}>✓ Claimed</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: "#FEF9C3", color: "#854D0E" }}>Pending</span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ═══ SUCCESS MODAL ═══ */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: "rgba(60,40,5,0.55)", backdropFilter: "blur(6px)" }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="w-full max-w-sm rounded-3xl p-8 text-center"
              style={{
                background: `linear-gradient(165deg,${G.cream},${G.creamDeep},${G.creamMid})`,
                border: `1.5px solid ${G.creamBorder}`,
                boxShadow: `0 24px 80px rgba(80,50,0,0.28)`,
              }}
            >
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: "#DCFCE7", border: "2px solid #BBF7D0" }}
              >
                <FaCheckCircle style={{ color: "#16A34A", fontSize: 36 }} />
              </div>
              <h2 className="text-2xl font-black mb-2" style={{ color: G.text }}>Tickets Booked!</h2>
              <p className="text-sm mb-1" style={{ color: G.textMid }}>
                <span className="font-bold" style={{ color: G.goldDeep }}>{bookedCount} ticket{bookedCount > 1 ? "s" : ""}</span> successfully booked for
              </p>
              <p className="font-extrabold mb-6" style={{ color: G.text }}>{campaign.title}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSuccess(false)}
                  className="flex-1 py-3 rounded-2xl font-bold text-sm transition hover:scale-105"
                  style={{ background: G.creamMid, color: G.goldDeep, border: `1.5px solid ${G.creamBorder}` }}
                >
                  Stay Here
                </button>
                <button
                  onClick={() => { setShowSuccess(false); navigate("/campaigns"); }}
                  className="flex-1 py-3 rounded-2xl font-extrabold text-sm transition hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})`,
                    color: "#3B2A0A",
                    boxShadow: `0 4px 14px ${G.goldGlow}`,
                  }}
                >
                  All Campaigns
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CampaignDetails;